import { Check, Eye } from 'lucide-react'
import { useId, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { usePatientsQuery } from '@/hooks/usePatientsQuery'
import { useDebounce } from '@/hooks/use-debounce'

export interface SelectedMember {
  id: string
  name: string
}

export function PreviewMemberPicker({
  value,
  onChange,
}: {
  value?: SelectedMember
  onChange: (value: SelectedMember | undefined) => void
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const query = useDebounce(search.trim(), 250)
  const members = usePatientsQuery(
    {
      limit: 25,
      sort_by: 'patient_name',
      sort_order: 'asc',
      filters: query
        ? [
            {
              id: 'name',
              field: 'patient_name',
              operator: 'contains',
              value: query,
            },
          ]
        : [],
    },
    { enabled: open, keepPreviousData: false }
  )
  const searching = search.trim() !== query || members.isPending
  const pickerId = useId()
  const selected = value
  const choose = (member: SelectedMember | undefined) => {
    onChange(member)
    setSearch('')
    setOpen(false)
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          aria-label="Preview member"
          aria-pressed={Boolean(selected)}
          aria-controls={pickerId}
          aria-expanded={open}
          className={cn(selected && 'text-primary-solid')}>
          <Eye className="size-4" />
          Preview
        </Button>
      </PopoverTrigger>
      <PopoverContent id={pickerId} align="end" className="w-72 p-0">
        {selected && (
          <div className="flex items-center gap-2 px-3 py-2">
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {selected.name}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground"
              aria-label="Exit preview"
              onClick={() => choose(undefined)}>
              Exit preview
            </Button>
          </div>
        )}
        <Command label="Find a member" shouldFilter={false}>
          <CommandInput
            placeholder="Find a member…"
            aria-label="Find a member"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {searching ? (
              <p role="status" className="p-3 text-sm text-muted-foreground">
                Searching…
              </p>
            ) : members.isError ? (
              <div role="alert" className="p-3 text-sm">
                Could not load members.{' '}
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => void members.refetch()}>
                  Retry
                </Button>
              </div>
            ) : (
              <>
                <CommandEmpty>No members found.</CommandEmpty>
                {members.data?.pages
                  .flatMap((page) => page.data)
                  .map((member) => {
                    const name =
                      [member.first_name, member.middle_name, member.last_name]
                        .filter(Boolean)
                        .join(' ') || 'Unnamed member'
                    return (
                      <CommandItem
                        key={member.id}
                        value={member.id}
                        onSelect={() => choose({ id: member.id, name })}>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{name}</span>
                          {member.date_of_birth && (
                            <span className="block text-xs text-muted-foreground">
                              {member.date_of_birth}
                            </span>
                          )}
                        </span>
                        {value?.id === member.id && (
                          <Check className="ml-auto size-4" />
                        )}
                      </CommandItem>
                    )
                  })}
                {members.hasNextPage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    disabled={members.isFetchingNextPage}
                    onClick={() => void members.fetchNextPage()}>
                    {members.isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </Button>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
